'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, Stack, TextField } from '@mui/material';
import { patchProspect } from '@repo/backoffice-api';
import { testEmail } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Renders a EditRepresentativeEmail body for a Modal component.
 *
 * @return {React.JSX.Element} The rendered EditRepresentativeEmail component.
 */

export const EditRepresentativeEmailBody = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const { prospectId, legalRepresentativeEmail } = useSelector((state: RootState) => state.prospectData);
  const router = useRouter();

  const [emailValue, setEmailValue] = useState(legalRepresentativeEmail || '');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEditRepresentativeEmail = async () => {
    if (!testEmail(emailValue)) {
      dispatch(
        showToast({
          text: 'El email ingresado no es válido',
          kind: 'error',
        }),
      );
      setError(true);
      return;
    }
    setLoading(true);
    let notAuth = false;
    try {
      const res = await patchProspect({ prospectId, legalRepresentativeEmail: emailValue });
      if (res.notAuth) {
        notAuth = true;
        dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
        throw new Error('User not authenticated');
      }
      if (res.softError || res.hardError) {
        dispatch(showToast({ text: 'Hubo un error al actualizar el email del representante legal', kind: 'error' }));
        throw new Error('Error updating representative email');
      }
      dispatch(
        showToast({ text: 'El email del representante legal ha sido actualizado exitosamente.', kind: 'success' }),
      );
      dispatch(hideModal());
      router.refresh();
    } catch (error) {
      console.error(error);
    } finally {
      if (notAuth) {
        dispatch(hideModal());
        router.push('/login');
        router.refresh();
      }
      setLoading(false);
    }
  };

  return (
    <Stack gap={4} minWidth="300px">
      <TextField
        label="Nuevo email"
        value={emailValue}
        placeholder={'Ingrese el nuevo email del representante legal'}
        error={error}
        helperText={error ? 'El email ingresado no es válido' : ''}
        onChange={(e) => {
          setEmailValue(e.target.value);
          setError(false);
        }}
      />
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" disabled={loading} onClick={() => dispatch(hideModal())}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={!testEmail(emailValue) || loading}
          onClick={(e) => {
            e.preventDefault();
            handleEditRepresentativeEmail();
          }}
        >
          <span>Confirmar</span>
        </Button>
      </Stack>
    </Stack>
  );
};
